import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { PlaceType } from 'components/commercetools-ui/atoms/auto-complete-google-places/types';

export type LocationInfo = {
  location?: string;
  lat?: number;
  lng?: number;
  state?: string;
  countryFullName?: string | undefined;
  distance?: { id: string; name: string };
};

type LocationState = {
  isLoadingGoogleMap?: boolean;
  isHydrated: boolean;
  options: readonly PlaceType[];
  isOptionsLoading: boolean;
  locationInput: string;
} & LocationInfo;

type LocationAction = {
  updateLocation: (info?: LocationInfo) => void;
  setLoadingGoogleMap: (loaded: boolean) => void;
  setHydrated: () => void;
  setOptions: (options: readonly PlaceType[]) => void;
  setOptionsLoading: (loading: boolean) => void;
  setLocationInput: (value: string) => void;
  setDistance: (value: { id: string; name: string }) => void;
};
export const defaultDistanceState = { id: '25', name: '25' };

const defaultState: LocationState = {
  lat: undefined,
  lng: undefined,
  location: undefined,
  state: undefined,
  isLoadingGoogleMap: false,
  isHydrated: false,
  options: [],
  isOptionsLoading: false,
  locationInput: '',
  countryFullName: undefined,
  distance: defaultDistanceState,
};

export const useLocationStore = create<LocationState & LocationAction>()(
  persist(
    devtools(
      immer((set) => ({
        ...defaultState,
        updateLocation(info) {
          set((state) => {
            state.lat = info?.lat;
            state.lng = info?.lng;
            state.location = info?.location;
            state.state = info?.state;
            state.countryFullName = info?.countryFullName;
          });
        },
        setHydrated() {
          set((state) => {
            state.isHydrated = true;
          });
        },
        setLoadingGoogleMap: (loaded) => set({ isLoadingGoogleMap: loaded }),
        setOptions: (options) =>
          set((state) => {
            state.options = [...options];
          }),
        setOptionsLoading: (loading) =>
          set((state) => {
            state.isOptionsLoading = loading;
          }),
        setLocationInput: (value) =>
          set((state) => {
            state.locationInput = value;
          }),
           setDistance: (value) =>
          set((state) => {
            state.distance = value;
          }),
      })),
      {
        name: 'Location Store',
        enabled: true,
      },
    ),
    {
      name: 'location-storage', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
      partialize: (state) => ({
        location: state.location,
        lat: state.lat,
        lng: state.lng,
        state: state.state,
        countryFullName: state.countryFullName,
        distance: state.distance,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    },
  ),
);
